import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { MessageSquarePlus, Send, CheckCircle2, ArrowUpRight, AlertTriangle, Search } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

const C = {
  text: "#3B2722", soft: "#765A4A", muted: "#a58f7c", border: "#E9DED0",
  panel: "#FFFDF9", panelSoft: "#FAF7F0", burgundy: "#8B1E1E", gold: "#C49A4A",
  green: "#4F7A52", red: "#A52222", amber: "#B7791F",
};

const SEV_TINT = {
  "Note": ["#F3ECE2", C.soft],
  "Warning": ["#FBF0DE", C.amber],
  "Final warning": ["#F7E5E5", C.red],
};
const STATUS_TINT = {
  open: ["#FBF0DE", C.amber, "Awaiting response"],
  responded: ["#E8F0E8", C.green, "Staff responded"],
  resolved: ["#E8F0E8", C.green, "Resolved"],
  escalated: ["#F7E5E5", C.red, "Escalated to HR"],
  closed: ["#F3ECE2", C.soft, "Closed by HR"],
};

const Chip = ({ tint, children, testid }) => (
  <span data-testid={testid} className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full whitespace-nowrap"
    style={{ background: tint[0], color: tint[1] }}>{children}</span>
);

export function HistoryPanel({ h, compact = false }) {
  const flagged = h.flag?.repeat_offender;
  return (
    <div className="rounded-xl border p-3" data-testid="q-history-panel"
      style={{ borderColor: flagged ? "#E8C9C9" : C.border, background: flagged ? "#FDF3F3" : C.panelSoft }}>
      <div className="flex items-center gap-2 flex-wrap">
        {flagged
          ? <><AlertTriangle size={14} style={{ color: C.red }} />
              <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: C.red }} data-testid="q-history-flag">Repeat pattern</span></>
          : <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: C.soft }}>Query record · last 12 months</span>}
        <span className="ml-auto text-[11px]" style={{ color: C.muted }}>
          {h.total} total · {h.last_90_days} in 90 days
          {h.days_since_last != null ? ` · last ${h.days_since_last}d ago` : " · none on record"}
        </span>
      </div>

      {flagged && (
        <ul className="mt-1.5 space-y-0.5">
          {h.flag.reasons.map((r, i) => (
            <li key={i} className="text-[12px] flex gap-1.5" style={{ color: C.text }}><span style={{ color: C.red }}>•</span>{r}</li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-1.5 mt-2">
        {Object.entries(h.by_severity || {}).filter(([, v]) => v > 0).map(([k, v]) => (
          <Chip key={k} tint={SEV_TINT[k] || SEV_TINT.Note}>{k} × {v}</Chip>
        ))}
        {h.open > 0 && <Chip tint={["#FBF0DE", C.amber]}>{h.open} still open</Chip>}
        {h.overdue > 0 && <Chip tint={["#F7E5E5", C.red]}>{h.overdue} overdue</Chip>}
        {h.total === 0 && <span className="text-[12px]" style={{ color: C.muted }}>Clean record — no queries in the last 12 months.</span>}
      </div>

      {!compact && (h.threads || []).length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="q-history-threads">
          {h.threads.map((t) => (
            <div key={t.id} className="rounded-lg border px-2.5 py-1.5" style={{ borderColor: C.border, background: C.panel }}>
              <div className="flex items-center gap-2 flex-wrap">
                <Chip tint={SEV_TINT[t.severity] || SEV_TINT.Note}>{t.severity}</Chip>
                <span className="text-[12.5px] font-medium" style={{ color: C.text }}>{t.subject}</span>
                <span className="ml-auto text-[11px]" style={{ color: C.muted }}>{(t.created_at || "").slice(0, 10)}</span>
              </div>
              <div className="text-[11px] mt-0.5" style={{ color: C.muted }}>
                {t.category} · raised by {t.raised_by} · {t.hr_outcome_label || t.status}
                {t.outcome_note ? ` — ${t.outcome_note}` : ""}
              </div>
            </div>
          ))}
        </div>
      )}
      {compact && (h.threads || []).length > 0 && (
        <div className="mt-2 text-[11px]" style={{ color: C.muted }}>
          Most recent: “{h.threads[0].subject}” ({h.threads[0].severity}, {(h.threads[0].created_at || "").slice(0, 10)} · {h.threads[0].hr_outcome_label || h.threads[0].status})
        </div>
      )}
    </div>
  );
}

export default function QueriesBoard({ showBranchFilter = false }) {
  const [data, setData] = useState({ rows: [], counts: {}, categories: [], severities: [] });
  const [recipients, setRecipients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState({ status: "", category: "", severity: "", location_id: "" });
  const [search, setSearch] = useState("");
  const [raise, setRaise] = useState(null);
  const [open, setOpen] = useState(null);
  const [reply, setReply] = useState("");
  const [closeForm, setCloseForm] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const d = await api.get("/hr/queries/tracking", { params: {
        status: filters.status || undefined, category: filters.category || undefined,
        severity: filters.severity || undefined, location_id: filters.location_id || undefined } });
      setData(d);
      if (open) setOpen(d.rows.find((r) => r.id === open.id) || open);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);
  useEffect(() => {
    api.get("/hr/queries/recipients").then(setRecipients).catch(() => {});
    if (showBranchFilter) api.get("/locations").then(setLocations).catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter((r) => `${r.subject} ${r.to_name} ${r.employee_no || ""}`.toLowerCase().includes(q));
  }, [data.rows, search]);

  const loadHistory = (userId, setter) => {
    setter(null);
    if (!userId) return;
    api.get("/hr/queries/history", { params: { user_id: userId } })
      .then(setter).catch(() => setter(null));
  };

  const submitRaise = async () => {
    if (!raise.to_id || !raise.subject.trim() || !raise.body.trim()) { toast.error("Staff member, subject and message are required"); return; }
    setBusy(true);
    try {
      await api.post("/hr/queries", { ...raise, due_by: raise.due_by || undefined });
      toast.success("Query raised — the staff member has been notified");
      setRaise(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    try { const r = await api.post(`/hr/queries/${open.id}/reply`, { body: reply }); setOpen(r); setReply(""); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const submitClose = async () => {
    if (!closeForm.outcome_note.trim()) { toast.error("An outcome note is required"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/hr/queries/${closeForm.id}/close`, closeForm);
      toast.success(closeForm.resolution === "escalated" ? "Escalated to HR" : "Query resolved");
      setOpen(r); setCloseForm(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const STATS = [
    ["Awaiting response", data.counts.open || 0, C.amber],
    ["Staff responded", data.counts.responded || 0, C.green],
    ["Overdue", data.counts.overdue || 0, C.red],
    ["Resolved", data.counts.resolved || 0, C.text],
    ["Escalated", data.counts.escalated || 0, C.burgundy],
  ];

  return (
    <div className="space-y-4" data-testid="queries-board">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {STATS.map(([l, v, c]) => (
          <div key={l} className="rounded-2xl border px-4 py-3" style={{ background: C.panel, borderColor: C.border }}
            data-testid={`q-stat-${l.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.soft }}>{l}</div>
            <div className="font-brand font-bold text-2xl" style={{ color: c }}>{v}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 border rounded-lg h-9 px-3 bg-white min-w-[220px]" style={{ borderColor: C.border }}>
          <Search size={14} style={{ color: C.soft }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search staff or subject…"
            className="bg-transparent flex-1 outline-none text-sm" data-testid="q-search" />
        </div>
        {[["status", ["open", "responded", "resolved", "escalated", "closed"], "All statuses"],
          ["category", data.categories, "All categories"],
          ["severity", data.severities, "All severities"]].map(([key, opts, label]) => (
          <select key={key} value={filters[key]} onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}
            data-testid={`q-filter-${key}`} className="h-9 rounded-md border px-3 text-sm bg-white" style={{ borderColor: C.border, color: C.text }}>
            <option value="">{label}</option>
            {(opts || []).map((o) => <option key={o} value={o}>{o[0].toUpperCase() + o.slice(1)}</option>)}
          </select>
        ))}
        {showBranchFilter && (
          <select value={filters.location_id} onChange={(e) => setFilters({ ...filters, location_id: e.target.value })}
            data-testid="q-filter-branch" className="h-9 rounded-md border px-3 text-sm bg-white" style={{ borderColor: C.border, color: C.text }}>
            <option value="">All branches</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        )}
        <Button className="ml-auto h-9 text-white" style={{ background: C.burgundy }} data-testid="q-raise-btn"
          onClick={() => setRaise({ to_id: "", subject: "", body: "", category: "Lateness", severity: "Note", due_by: "" })}>
          <MessageSquarePlus size={15} className="mr-1" /> Raise a query
        </Button>
      </div>

      <div className="rounded-2xl border overflow-hidden" style={{ borderColor: C.border, background: C.panel }}>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-xs uppercase border-b" style={{ background: "#EFE7D6", color: "#8a6f5c", borderColor: C.border }}>
            <th className="text-left px-4 py-3">Staff</th>
            <th className="text-left px-4 py-3">Subject</th>
            <th className="text-left px-4 py-3">Category</th>
            <th className="text-left px-4 py-3">Severity</th>
            <th className="text-left px-4 py-3">Due by</th>
            <th className="text-left px-4 py-3">Status</th>
            <th className="text-left px-4 py-3">Updated</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => {
              const st = STATUS_TINT[r.status] || STATUS_TINT.open;
              return (
                <tr key={r.id} onClick={() => { setOpen(r); setReply(""); }} data-testid={`q-row-${r.id}`}
                  className="border-b cursor-pointer hover:bg-[#FAF5EC]" style={{ borderColor: "#F5EFE5" }}>
                  <td className="px-4 py-3">
                    <button onClick={(e) => { e.stopPropagation(); loadHistory(r.to_id, setHistoryOf); }}
                      data-testid={`q-history-${r.id}`} className="text-left">
                      <span className="font-medium underline decoration-dotted" style={{ color: C.text }}>{r.to_name}</span>
                      <span className="block text-[11px]" style={{ color: C.muted }}>{r.employee_no || "—"} · {r.location_name}</span>
                    </button>
                  </td>
                  <td className="px-4 py-3 max-w-[260px] truncate" style={{ color: C.text }}>{r.subject}</td>
                  <td className="px-4 py-3" style={{ color: C.soft }}>{r.category}</td>
                  <td className="px-4 py-3"><Chip tint={SEV_TINT[r.severity] || SEV_TINT.Note}>{r.severity}</Chip></td>
                  <td className="px-4 py-3" style={{ color: r.overdue ? C.red : C.soft }}>
                    {r.due_by || "—"}{r.overdue && <AlertTriangle size={12} className="inline ml-1" />}
                  </td>
                  <td className="px-4 py-3"><Chip tint={st} testid={`q-status-${r.id}`}>{st[2]}</Chip></td>
                  <td className="px-4 py-3 text-[11px]" style={{ color: C.muted }}>{(r.updated_at || "").slice(0, 16).replace("T", " ")}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center" style={{ color: C.muted }} data-testid="q-empty">No queries yet.</td></tr>}
          </tbody>
        </table></div>
      </div>

      {/* Raise */}
      <Dialog open={!!raise} onOpenChange={(o) => !o && setRaise(null)}>
        <DialogContent className="max-w-lg" data-testid="q-raise-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand">Raise a query</DialogTitle>
            <DialogDescription>The staff member is notified in their portal and can reply there. You can only query your own branch team.</DialogDescription>
          </DialogHeader>
          {raise && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Staff member</Label>
                <select value={raise.to_id} onChange={(e) => { setRaise({ ...raise, to_id: e.target.value }); loadHistory(e.target.value, setHistory); }} data-testid="q-to"
                  className="w-full h-10 rounded-md border px-3 text-sm bg-white" style={{ borderColor: C.border }}>
                  <option value="">Select staff…</option>
                  {recipients.map((r) => <option key={r.user_id} value={r.user_id}>{r.name} — {r.position} ({r.branch})</option>)}
                </select>
              </div>
              {history && <HistoryPanel h={history} compact />}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Category</Label>
                  <select value={raise.category} onChange={(e) => setRaise({ ...raise, category: e.target.value })} data-testid="q-category"
                    className="w-full h-10 rounded-md border px-3 text-sm bg-white" style={{ borderColor: C.border }}>
                    {(data.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5"><Label>Severity</Label>
                  <select value={raise.severity} onChange={(e) => setRaise({ ...raise, severity: e.target.value })} data-testid="q-severity"
                    className="w-full h-10 rounded-md border px-3 text-sm bg-white" style={{ borderColor: C.border }}>
                    {(data.severities || []).map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Subject</Label>
                <Input value={raise.subject} onChange={(e) => setRaise({ ...raise, subject: e.target.value })} data-testid="q-subject" /></div>
              <div className="space-y-1.5"><Label>Message</Label>
                <Textarea rows={4} value={raise.body} onChange={(e) => setRaise({ ...raise, body: e.target.value })} data-testid="q-body" /></div>
              <div className="space-y-1.5"><Label>Respond by (optional)</Label>
                <Input type="date" value={raise.due_by} onChange={(e) => setRaise({ ...raise, due_by: e.target.value })} data-testid="q-due" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRaise(null)}>Cancel</Button>
            <Button onClick={submitRaise} disabled={busy} className="text-white" style={{ background: C.burgundy }} data-testid="q-raise-submit">
              <Send size={14} className="mr-1" /> Send query
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Thread */}
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-xl" data-testid="q-thread-dialog">
          {open && (<>
            <DialogHeader>
              <DialogTitle className="font-brand pr-8">{open.subject}</DialogTitle>
              <DialogDescription className="flex flex-wrap items-center gap-2 pt-1">
                <Chip tint={SEV_TINT[open.severity] || SEV_TINT.Note}>{open.severity}</Chip>
                <Chip tint={["#F3ECE2", C.soft]}>{open.category}</Chip>
                <Chip tint={STATUS_TINT[open.status] || STATUS_TINT.open}>{(STATUS_TINT[open.status] || STATUS_TINT.open)[2]}</Chip>
                <span style={{ color: C.muted }}>{open.to_name}{open.due_by ? ` · respond by ${open.due_by}` : ""}</span>
              </DialogDescription>
            </DialogHeader>

            <div className="max-h-64 overflow-y-auto space-y-2 pr-1" data-testid="q-thread-messages">
              {(open.messages || []).map((m) => (
                <div key={m.id} className="rounded-xl border px-3 py-2" style={{ borderColor: C.border, background: m.system ? "#F3ECE2" : C.panelSoft }}>
                  <div className="text-[11px] font-semibold" style={{ color: C.burgundy }}>{m.author_name}
                    <span className="ml-2 font-normal" style={{ color: C.muted }}>{(m.at || "").slice(0, 16).replace("T", " ")}</span></div>
                  <div className="text-sm whitespace-pre-wrap" style={{ color: C.text }}>{m.body}</div>
                </div>
              ))}
            </div>

            {["resolved", "escalated", "closed"].includes(open.status) ? (
              <div className="rounded-xl border px-3 py-2 text-sm" style={{ borderColor: C.border, background: "#F3ECE2", color: C.soft }} data-testid="q-closed-note">
                {open.hr_outcome_label
                  ? `HR decision — ${open.hr_outcome_label}: ${open.hr_outcome_note} (${open.hr_closed_by})`
                  : `Closed by ${open.closed_by} — ${open.outcome_note}`}
              </div>
            ) : (
              <>
                <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" data-testid="q-reply" />
                <DialogFooter className="flex-wrap gap-2">
                  <Button variant="outline" onClick={() => setCloseForm({ id: open.id, resolution: "escalated", outcome_note: "" })} data-testid="q-escalate-btn">
                    <ArrowUpRight size={14} className="mr-1" /> Escalate to HR
                  </Button>
                  <Button variant="outline" onClick={() => setCloseForm({ id: open.id, resolution: "resolved", outcome_note: "" })} data-testid="q-resolve-btn">
                    <CheckCircle2 size={14} className="mr-1" /> Mark resolved
                  </Button>
                  <Button onClick={sendReply} disabled={!reply.trim()} className="text-white" style={{ background: C.burgundy }} data-testid="q-reply-send">
                    <Send size={14} className="mr-1" /> Reply
                  </Button>
                </DialogFooter>
              </>
            )}
          </>)}
        </DialogContent>
      </Dialog>

      {/* Staff query record */}
      <Dialog open={!!historyOf} onOpenChange={(o) => !o && setHistoryOf(null)}>
        <DialogContent className="max-w-lg" data-testid="q-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand">Query record · {historyOf?.name}</DialogTitle>
            <DialogDescription>{historyOf?.employee_no} · rolling 12 months</DialogDescription>
          </DialogHeader>
          {historyOf && <HistoryPanel h={historyOf} />}
          <DialogFooter><Button variant="outline" onClick={() => setHistoryOf(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close / escalate */}
      <Dialog open={!!closeForm} onOpenChange={(o) => !o && setCloseForm(null)}>
        <DialogContent className="max-w-md" data-testid="q-close-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand">{closeForm?.resolution === "escalated" ? "Escalate to HR" : "Resolve query"}</DialogTitle>
            <DialogDescription>Record the outcome — it is added to the thread and both parties are notified.</DialogDescription>
          </DialogHeader>
          <Textarea rows={4} value={closeForm?.outcome_note || ""} data-testid="q-outcome-note"
            onChange={(e) => setCloseForm({ ...closeForm, outcome_note: e.target.value })}
            placeholder={closeForm?.resolution === "escalated" ? "Why is this going to HR?" : "What was agreed or corrected?"} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseForm(null)}>Cancel</Button>
            <Button onClick={submitClose} disabled={busy} className="text-white" style={{ background: C.burgundy }} data-testid="q-close-submit">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
